import { useMemo, useState } from "react";
import { creators } from "@/mocks/creators";
import type { Creator } from "@/pages/home/types";

const tierLabel: Record<Creator["tier"], string> = {
  head: "头部大V",
  quality: "优质达人",
  rising: "潜力新人",
};

const tierStyle: Record<Creator["tier"], string> = {
  head: "bg-primary-500 text-background-50",
  quality: "bg-accent-500 text-background-50",
  rising: "bg-secondary-500 text-background-50",
};

interface CreatorShowcaseProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}

function CreatorCard({ creator }: { creator: Creator }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-44 w-full overflow-hidden md:h-48">
        <img
          src={creator.cover}
          alt={creator.alt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${tierStyle[creator.tier]}`}
        >
          <i className="ri-verified-badge-line"></i>
          {tierLabel[creator.tier]}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-bold text-foreground-950">{creator.name}</h3>
          <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-800">
            {creator.category}
          </span>
        </div>
        <p className="mt-2 text-sm font-medium leading-relaxed text-primary-700">
          {creator.signature}
        </p>
        <p className="mt-3 flex-1 text-xs leading-relaxed text-foreground-600 md:text-sm">
          {creator.description}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {creator.focus.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-background-200/70 px-2 py-1 text-[11px] text-foreground-700"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function CreatorShowcase({ activeCategory, onCategoryChange }: CreatorShowcaseProps) {
  const [query, setQuery] = useState("");

  const tabs = useMemo(() => {
    const names = Array.from(new Set(creators.map((c) => c.category)));
    return ["全部", ...names];
  }, []);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return creators.filter((c) => {
      const matchCategory = activeCategory === "全部" || c.category === activeCategory;
      const matchKeyword =
        keyword.length === 0 ||
        c.name.toLowerCase().includes(keyword) ||
        c.category.toLowerCase().includes(keyword) ||
        c.signature.toLowerCase().includes(keyword) ||
        c.focus.some((f) => f.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, query]);

  return (
    <section id="creators" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-medium text-primary-700">
              <i className="ri-list-check-2"></i>
              创作者速览
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              头部大V、优质达人与潜力新人名单
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
              下面按内容领域整理了平台上有代表性的创作者档案。你可以用标签快速切换领域，
              也可以直接搜索名字或关键词，找到最贴近自己口味的那几位。
            </p>
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索创作者 / 领域 / 关键词"
              aria-label="搜索创作者"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => onCategoryChange(tab)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeCategory === tab
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <p className="mt-5 text-xs text-foreground-500">
          共找到 <strong className="font-semibold text-foreground-800">{filtered.length}</strong> 位创作者
          {activeCategory !== "全部" && ` · 领域：${activeCategory}`}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {filtered.map((creator) => (
              <CreatorCard key={creator.id} creator={creator} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-800">没有匹配的创作者</p>
            <p className="mt-1.5 text-xs text-foreground-500">
              换个关键词试试，或点击上方标签查看其他领域。
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                onCategoryChange("全部");
              }}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}